import { useEffect, useMemo, useState } from 'react';

import ResetIcon from '@/assets/all-icons/reset-icon';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import './styles/ArtworkAuctionEndDatePopup.css';

const MADRID_TIMEZONE = 'Europe/Madrid';

function getTimeZoneOffset(date, timeZone) {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return 0;
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  let partsArray;
  try {
    partsArray = formatter.formatToParts(date);
  } catch (error) {
    return 0;
  }

  const parts = partsArray.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return (asUTC - date.getTime()) / 60000;
}

function zonedDateToUtc({ year, month, day, hour, minute, second = 0, timeZone }) {
  const baseUTCDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const initialOffset = getTimeZoneOffset(baseUTCDate, timeZone);
  const firstPass = new Date(baseUTCDate.getTime() - initialOffset * 60000);

  const verifiedOffset = getTimeZoneOffset(firstPass, timeZone);
  if (verifiedOffset !== initialOffset) {
    return new Date(baseUTCDate.getTime() - verifiedOffset * 60000);
  }

  return firstPass;
}

function formatGmtOffset(offsetMinutes) {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const minutesLabel = minutes ? `:${String(minutes).padStart(2, '0')}` : '';
  return `GMT${sign}${String(hours).padStart(2, '0')}${minutesLabel}`;
}

function formatDifferenceLabel(diffMinutes) {
  if (diffMinutes === 0) {
    return '±0h';
  }

  const sign = diffMinutes > 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(diffMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const minutesSuffix = minutes ? ` ${minutes}m` : '';
  return `${sign}${hours}h${minutesSuffix}`;
}

export default function ArtworkAuctionEndDatePopup({
  open,
  onClose,
  formState,
  setFormState,
  formError,
  setFormError,
}) {
  const [dateValue, setDateValue] = useState(formState.auctionEndsDate || '');
  const [timeValue, setTimeValue] = useState(formState.auctionEndsTime || '');

  useEffect(() => {
    if (open) {
      setDateValue(formState.auctionEndsDate || '');
      setTimeValue(formState.auctionEndsTime || '');
    }
  }, [open, formState.auctionEndsDate, formState.auctionEndsTime]);

  const todayMin = useMemo(() => new Date().toISOString().split('T')[0], []);
  const userTimeZone = useMemo(() => {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return resolved || 'UTC';
  }, []);

  const timezoneMetrics = useMemo(() => {
    const now = new Date();
    const madridOffset = getTimeZoneOffset(now, MADRID_TIMEZONE);
    const userOffset = getTimeZoneOffset(now, userTimeZone);

    return {
      madridOffset,
      gmtLabel: formatGmtOffset(madridOffset),
      differenceLabel: formatDifferenceLabel(madridOffset - userOffset),
    };
  }, [userTimeZone]);

  const selectedSchedule = useMemo(() => {
    if (!dateValue || !timeValue) return null;

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);

    if ([year, month, day, hour, minute].some(Number.isNaN)) {
      return null;
    }

    return { year, month, day, hour, minute };
  }, [dateValue, timeValue]);

  const scheduledUtcDate = useMemo(() => {
    if (!selectedSchedule) return null;

    return zonedDateToUtc({
      ...selectedSchedule,
      timeZone: MADRID_TIMEZONE,
    });
  }, [selectedSchedule]);

  const hasScheduleError = useMemo(() => {
    if (!formError) return false;
    if (Array.isArray(formError)) {
      return formError.some(error => error?.error_code === 'INVALID_AUCTION_END_SCHEDULE');
    }
    return formError?.error_code === 'INVALID_AUCTION_END_SCHEDULE';
  }, [formError]);

  const isScheduleInPast = Boolean(scheduledUtcDate && scheduledUtcDate.getTime() <= Date.now());

  const clearScheduleError = () => {
    if (!setFormError) {
      return;
    }

    setFormError(prevError => {
      if (!prevError) return prevError;

      if (Array.isArray(prevError)) {
        const filteredErrors = prevError.filter(error => error?.error_code !== 'INVALID_AUCTION_END_SCHEDULE');
        return filteredErrors.length ? filteredErrors : null;
      }

      if (prevError?.error_code === 'INVALID_AUCTION_END_SCHEDULE') {
        return null;
      }

      return prevError;
    });
  };

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleBackgroundClick = event => {
    const card = event.currentTarget.querySelector('.generic-popup__card');
    if (card && card.contains(event.target)) {
      return;
    }

    handleClose();
  };

  const handleDateChange = event => {
    setDateValue(event.target.value);
    clearScheduleError();
  };

  const handleTimeChange = event => {
    setTimeValue(event.target.value);
    clearScheduleError();
  };

  const handleSubmit = () => {
    if (!selectedSchedule || !scheduledUtcDate || isScheduleInPast) {
      return;
    }

    const scheduledOffsetMinutes = getTimeZoneOffset(scheduledUtcDate, MADRID_TIMEZONE);
    const sign = scheduledOffsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(scheduledOffsetMinutes);
    const offHH = String(Math.floor(abs / 60)).padStart(2, '0');
    const offMM = String(abs % 60).padStart(2, '0');
    const endsAtWithOffset = `${dateValue}T${timeValue}${sign}${offHH}:${offMM}`;

    setFormState(prev => ({
      ...prev,
      auctionEndsDate: dateValue,
      auctionEndsTime: timeValue,
      artwork_auction: {
        ...(prev.artwork_auction ?? {}),
        ends_at: endsAtWithOffset,
      },
    }));

    handleClose();
  };

  const handleReset = () => {
    setDateValue('');
    setTimeValue('');
    setFormState(prev => ({
      ...prev,
      auctionEndsDate: '',
      auctionEndsTime: '',
      artwork_auction: {
        ...(prev.artwork_auction ?? {}),
        ends_at: null,
      },
    }));
  };

  const shouldShowScheduleError = hasScheduleError || isScheduleInPast;
  const isSubmitDisabled = !selectedSchedule || shouldShowScheduleError;

  return (
    <GenericPopup
      open={open}
      className="artwork-auction-end-date-popup remove-scrollbar"
      opacity
      onBackgroundClick={handleBackgroundClick}
    >
      <div className="artwork-auction-end-date-popup__header">
        <button
          type="button"
          className="artwork-auction-end-date-popup__reset"
          onClick={handleReset}
          aria-label="Reset auction end date"
        >
          <ResetIcon className="artwork-auction-end-date-popup__reset-icon" />
        </button>
        <h3 className="artwork-auction-end-date-popup__title">Set auction end</h3>
        <XIcon className="artwork-auction-end-date-popup__close" onClick={handleClose} />
      </div>

      <div className="artwork-auction-end-date-popup__body">
        <div className="artwork-auction-end-date-popup__inputs">
          <label className="artwork-auction-end-date-popup__field" htmlFor="artwork-auction-end-date-input">
            <Text className="artwork-auction-end-date-popup__label">
              Date
            </Text>
            <input
              id="artwork-auction-end-date-input"
              type="date"
              min={todayMin}
              value={dateValue}
              onChange={handleDateChange}
              className="artwork-auction-end-date-popup__input"
            />
          </label>

          <label className="artwork-auction-end-date-popup__field" htmlFor="artwork-auction-end-time-input">
            <Text className="artwork-auction-end-date-popup__label">
              Time
            </Text>
            <input
              id="artwork-auction-end-time-input"
              type="time"
              value={timeValue}
              onChange={handleTimeChange}
              className="artwork-auction-end-date-popup__input"
            />
          </label>
        </div>

        <div className="artwork-auction-end-date-popup__timezone">
          <Text className="artwork-auction-end-date-popup__timezone-title">
            Madrid ({timezoneMetrics.gmtLabel})
          </Text>
          <Text className="artwork-auction-end-date-popup__timezone-hint" extraSmall>
            Your timezone ({userTimeZone}) is {timezoneMetrics.differenceLabel} compared to Madrid.
          </Text>
        </div>

        {shouldShowScheduleError && (
          <Text className="artwork-auction-end-date-popup__error" extraSmall>
            Choose a future date and time.
          </Text>
        )}
      </div>

      <div className="artwork-auction-end-date-popup__footer">
        <ContinueButton onClick={handleSubmit} disabled={isSubmitDisabled}>
          Save
        </ContinueButton>
      </div>
    </GenericPopup>
  );
}
